import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Stack,
    Box,
    Chip,
    Divider,
    IconButton,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { format, parseISO } from "date-fns";

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        return format(parseISO(dateStr), "MM/dd/yyyy");
    } catch {
        return dateStr;
    }
}

function linkedLabel(ecn) {
    if (ecn.is_generic) return "Generic";
    if (ecn.project_detail) {
        return `${ecn.project_detail.number} · ${ecn.project_detail.name}`;
    }
    if (ecn.quote_detail) {
        return `${ecn.quote_detail.number} · ${ecn.quote_detail.name}`;
    }
    return "—";
}

function userLabel(detail) {
    if (!detail) return "—";
    return detail.full_name || detail.username;
}

const AFFECTS_FIELDS = [
    { key: "affects_cost", label: "Cost" },
    { key: "affects_schedule", label: "Schedule" },
    { key: "affects_procurement", label: "Procurement" },
    { key: "affects_installation", label: "Installation" },
    { key: "affects_fabrication", label: "Fabrication" },
];

function Field({ label, value }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {value || "—"}
            </Typography>
        </Box>
    );
}

export default function ECNViewDialog({ open, onClose, ecn }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    if (!ecn) return null;

    const affects = AFFECTS_FIELDS.filter(({ key }) => ecn[key]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            fullScreen={fullScreen}
        >
            <DialogTitle>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    {ecn.number}
                    {fullScreen && (
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Field label="Title" value={ecn.title} />
                    <Field label="Linked To" value={linkedLabel(ecn)} />

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                            label={ecn.is_external ? "External" : "Internal"}
                            size="small"
                            color={ecn.is_external ? "secondary" : "default"}
                        />
                        {affects.map(({ key, label }) => (
                            <Chip key={key} label={`Affects: ${label}`} size="small" variant="outlined" />
                        ))}
                    </Stack>

                    <Divider />

                    <Field label="Requested By" value={userLabel(ecn.requested_by_detail)} />
                    <Field label="Issued By" value={userLabel(ecn.issued_by_detail)} />
                    <Field label="Approved By" value={userLabel(ecn.approved_by_detail)} />

                    <Divider />

                    <Field label="Description" value={ecn.description} />
                    <Field label="Reason for Change" value={ecn.reason_for_change} />
                    <Field label="Comments" value={ecn.comments} />

                    <Divider />

                    <Typography variant="caption" color="text.secondary">
                        Created {formatDate(ecn.created)}
                        {ecn.created_by_detail
                            ? ` by ${userLabel(ecn.created_by_detail)}`
                            : ""}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
